import { StableChildren } from '@prophecy/utils/react/StableChildren';
import { createContext } from '@prophecy/utils/react/useZustandContext';
import { noop } from 'lodash-es';
import React, { useMemo, useState } from 'react';

import { LocalPreferenceKeys, ProjectPreferenceKeys, SetPreference } from './types';
import { useSetPreferenceHistory, usePreference } from './util';

const LOCAL_PREFERENCE_STORE_KEY = 'prophecy.localPreferences';

export type SqlBrowserSettingsType = {
  showModels: boolean;
  showPackages: boolean;
};

export type ProjectPreference = Partial<{
  [ProjectPreferenceKeys.sqlBrowserSettings]: SqlBrowserSettingsType;
  [ProjectPreferenceKeys.expandedEntities]: string[];
  [key: `${string}_${ProjectPreferenceKeys.expandedEntities}`]: string[];
  [ProjectPreferenceKeys.projectBrowserDefaultTab]: string;
}>;

type LocalPreferenceValues = Partial<{
  [LocalPreferenceKeys.projectPreferences]: Record<string, ProjectPreference>;
}>;

type LocalPreferenceContextType = {
  preferences: LocalPreferenceValues;
  setPreference: SetPreference<LocalPreferenceValues>;
};

const { Context: LocalPreferenceContext, Provider: LocalPreferenceZustandProvider } =
  createContext<LocalPreferenceContextType>({
    preferences: {},
    setPreference: noop
  });

function useLocalPreferenceStore() {
  const stateString = localStorage.getItem(LOCAL_PREFERENCE_STORE_KEY) || '{}';
  const [localPreferences, setLocalPreferences] = useState<LocalPreferenceValues>(JSON.parse(stateString));

  const persistPreference = (preferences: LocalPreferenceValues) => {
    localStorage.setItem(LOCAL_PREFERENCE_STORE_KEY, JSON.stringify(preferences));
  };

  const setPreference = useSetPreferenceHistory(localPreferences, setLocalPreferences, persistPreference);

  return { localPreferences, setPreference };
}

export function LocalPreferenceProvider({ children }: { children: React.ReactNode }) {
  const { localPreferences, setPreference } = useLocalPreferenceStore();
  const memoizedContextValue = useMemo(() => {
    return { preferences: localPreferences, setPreference };
  }, [localPreferences, setPreference]);
  return (
    <LocalPreferenceZustandProvider value={memoizedContextValue}>
      <StableChildren>{children}</StableChildren>
    </LocalPreferenceZustandProvider>
  );
}

export function useLocalPreference<T extends keyof LocalPreferenceValues>(key: T) {
  return usePreference(LocalPreferenceContext, key);
}
